import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Chip,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  VerifiedUser as SuperUserIcon,
  Edit as WriteIcon,
  Visibility as ReadIcon,
  Star as OwnerIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
} from '@material-ui/icons';
import { getProductLabel } from '../../../../utils';

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  },
  tableContainer: {
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e5e7eb',
  },
  tableHeader: {
    backgroundColor: '#f8fafc',
    color: '#374151',
    fontWeight: 700,
    fontSize: '1.1rem',
    padding: theme.spacing(2),
    borderBottom: '2px solid #e5e7eb',
  },
  tableRow: {
    transition: 'all 0.2s ease',
    '&:hover': { backgroundColor: '#f9fafb' },
  },
  permissionChip: {
    margin: theme.spacing(0.5),
    textTransform: 'capitalize',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '32px',
    borderRadius: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  superUserChip: { backgroundColor: '#6366f1', color: 'white' },
  ownerChip: { backgroundColor: '#ec4899', color: 'white' },
  writeChip: { backgroundColor: '#06b6d4', color: 'white' },
  readChip: { backgroundColor: '#10b981', color: 'white' },
  pendingChip: { backgroundColor: '#f59e0b', color: 'white' },
  requestButton: {
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 2.5),
    backgroundColor: '#f97316',
    color: 'white',
    '&:hover': { backgroundColor: '#ea580c' },
  },
  actionText: {
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  productOwnerChip: {
    margin: theme.spacing(0.25),
    fontSize: '0.7rem',
    height: '24px',
    backgroundColor: '#e0f2fe',
    color: '#0277bd',
    border: '1px solid #b3e5fc',
    fontWeight: 500,
  },
}));

interface UserPermission {
  product: string;
  permission: string;
  isPending?: boolean;
  pendingPermission?: string;
  productOwners?: string[];
}

interface PermissionsTableProps {
  permissions: UserPermission[];
  hasPendingApprovals: boolean;
  onRequestPermission: (
    product: string,
    permissionType: 'write' | 'admin',
  ) => void;
}

export const PermissionsTable: React.FC<PermissionsTableProps> = ({
  permissions,
  hasPendingApprovals,
  onRequestPermission,
}) => {
  const classes = useStyles();
  const [requestMenu, setRequestMenu] = React.useState<{
    anchorEl: HTMLElement | null;
    product: string;
  }>({
    anchorEl: null,
    product: '',
  });

  const getPermissionIcon = (permission: string) => {
    const lowerPerm = permission.toLowerCase();
    switch (lowerPerm) {
      case 'super_admin':
        return <SuperUserIcon style={{ fontSize: '1rem', marginRight: 4 }} />;
      case 'admin':
        return <OwnerIcon style={{ fontSize: '1rem', marginRight: 4 }} />;
      case 'write':
        return <WriteIcon style={{ fontSize: '1rem', marginRight: 4 }} />;
      case 'read':
        return <ReadIcon style={{ fontSize: '1rem', marginRight: 4 }} />;
      default:
        return undefined;
    }
  };

  const getPermissionChipClass = (
    permission: string,
    isPending?: boolean,
  ): string => {
    if (isPending) return `${classes.permissionChip} ${classes.pendingChip}`;
    const lowerPerm = permission.toLowerCase();
    const baseClass = classes.permissionChip;
    switch (lowerPerm) {
      case 'super_admin':
        return `${baseClass} ${classes.superUserChip}`;
      case 'admin':
        return `${baseClass} ${classes.ownerChip}`;
      case 'write':
        return `${baseClass} ${classes.writeChip}`;
      case 'read':
        return `${baseClass} ${classes.readChip}`;
      default:
        return baseClass;
    }
  };

  const getPermissionLabel = (permission: string): string => {
    return permission.replace(/_/g, ' ').toUpperCase();
  };

  const renderActionButton = (userPerm: UserPermission) => {
    const permissionType = userPerm.permission.toLowerCase();

    if (userPerm.isPending) {
      return (
        <Typography
          variant="body2"
          className={classes.actionText}
          style={{ color: '#f59e0b' }}
        >
          ⏳ Pending{' '}
          {getPermissionLabel(
            userPerm.pendingPermission || userPerm.permission,
          )}{' '}
          Access
        </Typography>
      );
    }

    if (permissionType === 'super_admin') {
      return (
        <Typography
          variant="body2"
          className={classes.actionText}
          style={{ color: '#6366f1' }}
        >
          <SuperUserIcon style={{ marginRight: 8 }} />
          Super Admin Access
        </Typography>
      );
    }

    if (permissionType === 'admin') {
      return (
        <Typography
          variant="body2"
          className={classes.actionText}
          style={{ color: '#059669' }}
        >
          <OwnerIcon style={{ marginRight: 8 }} />
          Product Admin
        </Typography>
      );
    }

    if (permissionType === 'write') {
      return (
        <Button
          variant="contained"
          size="small"
          className={classes.requestButton}
          onClick={() => onRequestPermission(userPerm.product, 'admin')}
        >
          Request Admin Access
        </Button>
      );
    }

    return (
      <>
        <Button
          variant="contained"
          size="small"
          className={classes.requestButton}
          onClick={e =>
            setRequestMenu({
              anchorEl: e.currentTarget,
              product: userPerm.product,
            })
          }
          endIcon={<ExpandMoreIcon />}
        >
          Request Access
        </Button>
        <Menu
          anchorEl={requestMenu.anchorEl}
          open={
            Boolean(requestMenu.anchorEl) &&
            requestMenu.product === userPerm.product
          }
          onClose={() => setRequestMenu({ anchorEl: null, product: '' })}
        >
          <MenuItem
            onClick={() => {
              onRequestPermission(userPerm.product, 'write');
              setRequestMenu({ anchorEl: null, product: '' });
            }}
          >
            <WriteIcon style={{ marginRight: 8, fontSize: '1rem' }} />
            Request Write Access
          </MenuItem>
          <MenuItem
            onClick={() => {
              onRequestPermission(userPerm.product, 'admin');
              setRequestMenu({ anchorEl: null, product: '' });
            }}
          >
            <OwnerIcon style={{ marginRight: 8, fontSize: '1rem' }} />
            Request Admin Access
          </MenuItem>
        </Menu>
      </>
    );
  };

  return (
    <Card className={classes.card}>
      <TableContainer className={classes.tableContainer}>
        <Table aria-label="user permissions table">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Product</TableCell>
              <TableCell className={classes.tableHeader}>
                Current Permission
              </TableCell>
              {hasPendingApprovals && (
                <TableCell className={classes.tableHeader}>
                  Product Owners
                </TableCell>
              )}
              <TableCell className={classes.tableHeader} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map((userPerm, index) => (
              <TableRow
                key={`${userPerm.product}-${index}`}
                className={classes.tableRow}
                hover
              >
                <TableCell
                  style={{ fontWeight: 600, color: '#374151', padding: 16 }}
                >
                  {getProductLabel(userPerm.product)}
                </TableCell>
                <TableCell style={{ padding: 16 }}>
                  <Chip
                    icon={getPermissionIcon(
                      userPerm.isPending
                        ? userPerm.pendingPermission || userPerm.permission
                        : userPerm.permission,
                    )}
                    label={
                      userPerm.isPending
                        ? `PENDING ${getPermissionLabel(
                            userPerm.pendingPermission || userPerm.permission,
                          )}`
                        : getPermissionLabel(userPerm.permission)
                    }
                    className={getPermissionChipClass(
                      userPerm.isPending
                        ? userPerm.pendingPermission || userPerm.permission
                        : userPerm.permission,
                      userPerm.isPending,
                    )}
                  />
                </TableCell>
                {hasPendingApprovals && (
                  <TableCell style={{ padding: 16 }}>
                    {userPerm.isPending && userPerm.productOwners?.length ? (
                      <Box
                        display="flex"
                        flexDirection="column"
                        style={{ gap: '4px' }}
                      >
                        {userPerm.productOwners.map((owner, idx) => (
                          <Chip
                            key={idx}
                            icon={<PersonIcon style={{ fontSize: '0.8rem' }} />}
                            label={owner}
                            size="small"
                            className={classes.productOwnerChip}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        style={{
                          color: '#9ca3af',
                          fontSize: '0.875rem',
                          fontStyle: 'italic',
                        }}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                )}
                <TableCell align="center" style={{ padding: 16 }}>
                  {renderActionButton(userPerm)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};
